import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useCountries, useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {getUserCreationProgress, getUsersCreationResult} from 'src/openapi/admin/apis/BulkUserCreationApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ProcessedUserItem} from 'src/openapi/admin/models/ProcessedUserItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const ImportUserResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getUserStatusByCode} = useUserStatuses();
    const {getUserTypeByCode} = useUserTypes();
    const {getCountryByCode} = useCountries();
    const {newsletterLabels} = useNewsletterDictionary();

    const getPaginatedResults = useCallback(
        (bulkResult: ProcessedUserItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: ProcessedUserItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        {res.userId ? (
                            <UserLink
                                userId={res.userId}
                                caption={res.email}
                                blank={true}
                                place={`${TOOL_TITLE} Result`}
                            />
                        ) : (
                            <EllipsisTextHint title={res.email}>
                                {res.email}
                            </EllipsisTextHint>
                        )}
                    </Cell>
                    <Cell>{!!res.priorAccountId && !!res.priorAccountName && (
                        <AccountLink
                            accountId={res.priorAccountId}
                            accountName={res.priorAccountName}
                            blank={true}
                            place={`${TOOL_TITLE} Result`}
                        />
                    )}
                    </Cell>
                    <Cell>
                        {getUserTypeByCode(res.typeCode)?.description} ({
                            getUserStatusByCode(res.statusCode)?.description
                        })
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors} tooltip={res.jobInfo}/>
                    </Cell>
                </Row>
            )),
        [getUserStatusByCode, getUserTypeByCode],
    );

    return (
        <BulkActionResultModal<ProcessedUserItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Result of the import of users to {bulkResult.itemList && bulkResult.itemList[0] ? (
                        <AccountLink
                            accountId={bulkResult.itemList[0].targetAccountId}
                            accountName={bulkResult.itemList[0].targetAccountName}
                            blank={true}
                            place={`${TOOL_TITLE} Result`}
                        />
                    ) : null} account
                    </b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            userId: item.userId ? item.userId.toString() : '',
                            email: item.email,
                            firstName: item.firstName,
                            lastName: item.lastName,
                            userType: getUserTypeByCode(item.typeCode)?.description,
                            userStatus: getUserStatusByCode(item.statusCode)?.description,
                            targetAccountId: item.targetAccountId.toString(),
                            targetAccountName: item.targetAccountName,
                            priorAccountId: item.priorAccountId?.toString(),
                            priorAccountName: item.priorAccountName,
                            country: item.countryCode
                                ? getCountryByCode(item.countryCode)?.description
                                : '--',
                            group: item.groupName,
                            jobTitle: item.jobTitle,
                            city: item.city,
                            newsletters: item.newsletterType.map(type => newsletterLabels[type]).join('; ') ||
                                'No Newsletter',
                            notes: item.notes,
                            actionStatus: item.actionStatus,
                            jobInfo: item.jobInfo?.join('. '),
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={200}>Prior Account</Cell>
                        <Cell width={200}>User State</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUserCreationProgress({id}))}
            getResult={() => apiDispatch(getUsersCreationResult({id}))}

            sortComparator={(a, b) => a.email.localeCompare(b.email)}
        />
    );
};
